import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { TrendingUp, Target, DollarSign, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ROISection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const { language } = useLanguage();

  const content = {
    en: {
      overline: 'Return on Investment',
      headline: 'Measurable',
      headlineAccent: 'Growth',
      subheadline: "We don't just build campaigns — we deliver measurable results.",
      badge: 'Based on 400+ client projects',
      metrics: [
        { icon: TrendingUp, range: '+80% to +180%', label: 'ROI Increase' },
        { icon: Target, range: '+30% to +70%', label: 'Conversion Rate' },
        { icon: DollarSign, range: '-25% to -55%', label: 'Cost Per Lead' },
      ],
      focusText: 'Every strategy optimized for maximum return.',
    },
    tr: {
      overline: 'Yatırım Getirisi',
      headline: 'Ölçülebilir',
      headlineAccent: 'Büyüme',
      subheadline: 'Sadece kampanya oluşturmuyoruz — ölçülebilir sonuçlar sağlıyoruz.',
      badge: '400+ müşteri projesine dayalı',
      metrics: [
        { icon: TrendingUp, range: '+80% ile +180%', label: 'ROI Artışı' },
        { icon: Target, range: '+30% ile +70%', label: 'Dönüşüm Oranı' },
        { icon: DollarSign, range: '-%25 ile -%55', label: 'Lead Başına Maliyet' },
      ],
      focusText: 'Her strateji maksimum getiri için optimize edilmiştir.',
    },
  };

  const t = content[language] || content.en;

  return (
    <section className="py-16 md:py-20 bg-[#F9FAFB]" ref={ref}>
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="text-[#E60000] text-sm uppercase tracking-[0.2em] font-bold">{t.overline}</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mt-3 tracking-tight">
            {t.headline} <span className="text-[#E60000]">{t.headlineAccent}</span>
          </h2>
          <p className="text-[#6B7280] text-base mt-3">{t.subheadline}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-[#0A0A0A] rounded-2xl p-6 md:p-10"
        >
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-white text-sm font-medium">
              <CheckCircle2 size={16} className="text-[#E60000]" />
              {t.badge}
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {t.metrics.map((metric, index) => (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, y: 10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                className="bg-white/5 rounded-xl p-5 text-center border border-white/10"
                data-testid={`roi-metric-${index}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#E60000]/20 flex items-center justify-center mx-auto mb-3">
                  <metric.icon className="w-5 h-5 text-[#E60000]" />
                </div>
                <p className="font-heading text-2xl md:text-3xl font-black text-white mb-1">
                  {metric.range}
                </p>
                <p className="text-gray-400 text-sm">{metric.label}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-gray-400 text-sm mt-8">{t.focusText}</p>
        </motion.div>
      </div>
    </section>
  );
};

export default ROISection;
